import * as XLSX from 'xlsx-js-style';
import { SiteNode } from '../types/types';

/**
 * Determines the display type for export based on node properties
 */
const getExportType = (node: SiteNode): string => {
  if (node.type === 'machine') {
    return 'Charging Station';
  }

  // Actor, level, and location types all display as 'Location'
  if (node.type === 'actor' || node.type === 'level' || node.type === 'location') {
    return 'Location';
  }

  return node.type;
};

/**
 * Returns the session display value based on node type:
 * - Locations: show machinesInSession count only if > 0
 * - Charging Stations: show sessionId only if active
 */
const getSessionValue = (node: SiteNode): string => {
  if (node.type === 'machine') {
    return node.sessionId && node.sessionId !== '0' ? node.sessionId : '';
  }
  // Location nodes: show count of active sessions only if > 0
  return node.machinesInSession && node.machinesInSession > 0 ? node.machinesInSession.toString() : '';
};

/**
 * Flattens hierarchical site data for Excel export
 */
const flattenSiteData = (nodes: SiteNode[], level = 0, parentPath = '', parentLocationName = ''): ExportRow[] => {
  const flattened: ExportRow[] = [];

  nodes.forEach((node) => {
    const currentPath = parentPath ? `${parentPath} > ${node.name}` : node.name;
    const indent = '  '.repeat(level); // Visual indentation for hierarchy
    const isMachine = node.type === 'machine';

    // Location column: for Charging Station rows show parent location name, otherwise show own name with indent
    const locationValue = isMachine ? parentLocationName : `${indent}${node.name}`;

    // Track location name for children: use own name if this is a location-type node
    const locationNameForChildren = isMachine ? parentLocationName : node.name;

    flattened.push({
      'Full Path': currentPath,
      'Type': getExportType(node),
      'Location': locationValue,
      'Charging Station Name': isMachine ? node.name : '',
      'EVSE ID': node.evseId?.toString() || '',
      'Machine ID': node.machineId?.toString() || '',
      'Status': node.status || '',
      'Last Communication': node.lastCommunicationAt || '',
      'Sessions': getSessionValue(node),
      'Machines in Session': node.machinesInSession?.toString() || '',
      'Total Machines': node.totalMachines?.toString() || '',
      'Offered [A]': node.offered || '',
      'Actual [A]': node.actual || '',
      'Allocated Total [A]': node.allocated?.total?.toString() || '',
      'Allocated Percentage': node.allocated?.percentage ? `${node.allocated.percentage}%` : '',
      'Current [A]': node.currentA !== null && node.currentA !== undefined ? node.currentA.toString() : '',
      'Buffer [A]': node.bufferA !== null && node.bufferA !== undefined ? node.bufferA.toString() : '',
      'Power [kW]': node.powerKw !== null && node.powerKw !== undefined ? node.powerKw.toString() : '',
      'Buffer [kW]': node.bufferKw !== null && node.bufferKw !== undefined ? node.bufferKw.toString() : '',
    });

    // Recursively add children
    if (node.children && node.children.length > 0) {
      flattened.push(...flattenSiteData(node.children, level + 1, currentPath, locationNameForChildren));
    }
  });

  return flattened;
};

interface ExportRow {
  'Full Path': string;
  'Type': string;
  'Location': string;
  'Charging Station Name': string;
  'EVSE ID': string;
  'Machine ID': string;
  'Status': string;
  'Last Communication': string;
  'Sessions': string;
  'Machines in Session': string;
  'Total Machines': string;
  'Offered [A]': string;
  'Actual [A]': string;
  'Allocated Total [A]': string;
  'Allocated Percentage': string;
  'Current [A]': string;
  'Buffer [A]': string;
  'Power [kW]': string;
  'Buffer [kW]': string;
}

/**
 * Calculate optimal column width based on content
 */
const calculateColumnWidth = (data: ExportRow[], key: keyof ExportRow, headerLength: number): number => {
  const maxLength = data.reduce((max, row) => {
    const cellLength = row[key]?.toString().length || 0;
    return Math.max(max, cellLength);
  }, headerLength);

  // Add some padding and cap at reasonable maximum
  return Math.min(Math.max(maxLength + 2, 10), 60);
};

/**
 * Export energy management data to Excel format
 */
export const exportEnergyDataToExcel = (
  data: SiteNode[],
  filename?: string
) => {
  if (!data || data.length === 0) {
    throw new Error('No energy management data to export');
  }

  // Flatten the hierarchical data
  const excelData = flattenSiteData(data);

  // Create workbook and worksheet
  const workbook = XLSX.utils.book_new();
  const worksheet = XLSX.utils.json_to_sheet(excelData);

  // Auto-resize columns based on content
  const headers: (keyof ExportRow)[] = [
    'Full Path', 'Type', 'Location', 'Charging Station Name',
    'EVSE ID', 'Machine ID', 'Status', 'Last Communication', 'Sessions',
    'Machines in Session', 'Total Machines', 'Offered [A]', 'Actual [A]',
    'Allocated Total [A]', 'Allocated Percentage', 'Current [A]', 'Buffer [A]',
    'Power [kW]', 'Buffer [kW]'
  ];

  const columnWidths = headers.map(header => ({
    wch: calculateColumnWidth(excelData, header, header.length)
  }));

  worksheet['!cols'] = columnWidths;

  // Make header row bold with styling
  const range = XLSX.utils.decode_range(worksheet['!ref'] || 'A1');
  for (let col = range.s.c; col <= range.e.c; col++) {
    const cellAddress = XLSX.utils.encode_cell({ r: 0, c: col });
    const cell = worksheet[cellAddress];
    if (!cell) continue;

    // Apply bold font and center alignment to header cells
    cell.s = {
      font: {
        bold: true,
        sz: 11,
        color: { rgb: "000000" }
      },
      alignment: {
        horizontal: 'center',
        vertical: 'center',
        wrapText: false
      },
      fill: {
        fgColor: { rgb: "E7E6E6" }
      },
      border: {
        top: { style: 'thin', color: { rgb: "000000" } },
        bottom: { style: 'thin', color: { rgb: "000000" } },
        left: { style: 'thin', color: { rgb: "000000" } },
        right: { style: 'thin', color: { rgb: "000000" } }
      }
    };
  }

  // Apply background color to Location rows
  for (let rowIdx = 1; rowIdx <= excelData.length; rowIdx++) {
    if (excelData[rowIdx - 1]['Type'] === 'Location') {
      for (let col = range.s.c; col <= range.e.c; col++) {
        const cellAddress = XLSX.utils.encode_cell({ r: rowIdx, c: col });
        if (!worksheet[cellAddress]) {
          worksheet[cellAddress] = { v: '', t: 's' };
        }
        worksheet[cellAddress].s = {
          ...worksheet[cellAddress].s,
          fill: { fgColor: { rgb: 'F2F2F2' } }
        };
      }
    }
  }

  // Add auto-filter to header row
  worksheet['!autofilter'] = { ref: `A1:${XLSX.utils.encode_col(headers.length - 1)}${excelData.length + 1}` };

  // Add the worksheet to workbook
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Energy Management');

  // Generate filename with current date and time if not provided
  const exportFilename = filename || `energy-management-${formatDateForFilename(new Date())}.xlsx`;

  // Write and download the file
  XLSX.writeFile(workbook, exportFilename);
};

/**
 * Format date for filename (YYYY-MM-DD_HH-mm-ss)
 */
const formatDateForFilename = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');

  return `${year}-${month}-${day}_${hours}-${minutes}-${seconds}`;
};
